/**
 * PlatformFichaFinanzasNodo — bloques financieros del inspector de un nodo
 * (centro de operaciones, 2026-06). Hace del panel derecho del árbol "el lugar
 * donde se ve todo" de un nodo, sin saltar al tab Finanzas.
 *
 * Reusa endpoints que YA existen (cero endpoints nuevos). Cuatro bloques, todos
 * de SOLO LECTURA, montos en céntimos formateados con MonedaService:
 *   - Cuenta corriente: saldo vivo (a favor / debe) + historial bajo demanda.
 *   - Producción / KPIs: ventas, premios, GGR, recargas, retiros (últimos 30d).
 *   - Cortes y liquidaciones: último corte (estado conformidad) + liquidaciones.
 *   - Obligaciones y adelantos: compromisos efectivos + transferencias activas.
 *
 * LAZY: el inspector pinta los acordeones COLAPSADOS con un placeholder; la data
 * se trae al expandir (un fetch por bloque abierto). Así no se ralentiza el
 * panel — que ya hace 3 fetches al abrir un nodo — ni se cargan datos que el
 * admin quizá no mire. Cada bloque cachea su HTML para no re-fetchear al
 * colapsar/expandir.
 */

import { App } from '../admin.js';
import { icon } from '../utils/icon.js';
import { t } from '../services/i18n-admin.js';
import { MonedaService } from '../services/moneda-service.js';
import { escapeHtmlParaInyeccionSegura } from '../utils/escape-html-para-inyeccion-segura.js';
import { canalesDeMatrizParaMostrar } from '@shared/config/games/lotowheel/canal-de-venta.js';
import { etiquetaDeCanalDeComision } from '@shared/config/games/lotowheel/canales-de-comision-del-contrato.js';

const ESC = escapeHtmlParaInyeccionSegura;

// Bloques disponibles: clave → metadata para el acordeón + el cargador.
const BLOQUES = {
    cuenta: { titulo: t('Cuenta corriente'), icono: 'dollar-sign' },
    produccion: { titulo: t('Producción (acumulado)'), icono: 'bar-chart-2' },
    cortes: { titulo: t('Cortes y liquidaciones'), icono: 'file-text' },
    obligaciones: { titulo: t('Obligaciones y adelantos'), icono: 'list' },
};

export const PlatformFichaFinanzasNodo = {
    // Moneda del nodo en foco — la setea el inspector al renderizar (para formato).
    _moneda: null,

    /**
     * HTML de los 4 acordeones financieros, colapsados y lazy. El inspector de
     * nodo lo inyecta tras el bloque "Juegos y Sorteos".
     * @param {number} nodoId
     * @param {string} moneda - código ISO del nodo (para MonedaService.format).
     */
    render(nodoId, moneda, claves = Object.keys(BLOQUES)) {
        this._moneda = moneda || null;
        return claves.map(clave => this._acordeon(clave, nodoId)).join('');
    },

    _acordeon(clave, nodoId) {
        const meta = BLOQUES[clave];
        return `
            <div class="nodo-detail-section nodo-detail-accordion nodo-finanzas-acordeon"
                 data-action="finanzas-nodo:toggle" data-bloque="${clave}" data-id="${nodoId}">
                <h4 class="nodo-detail-accordion-header">
                    <span class="nodo-detail-accordion-arrow">${icon('chevron-down')}</span>
                    ${icon(meta.icono)} ${ESC(meta.titulo)}
                </h4>
                <div class="nodo-detail-accordion-body" data-bloque-body="${clave}">
                    <p class="text-muted" style="font-size:13px;margin:0">${t('Abrí para cargar…')}</p>
                </div>
            </div>
        `;
    },

    // Toggle + lazy load. Llamado por el binding finanzas-nodo:toggle.
    async toggle(el) {
        const seccion = el.closest('.nodo-finanzas-acordeon');
        if (!seccion) return;
        const abierto = seccion.classList.toggle('open');
        if (!abierto) return; // al cerrar no recargamos
        const clave = el.dataset.bloque;
        const nodoId = Number(el.dataset.id);
        const body = seccion.querySelector(`[data-bloque-body="${clave}"]`);
        if (!body || body.dataset.cargado === '1') return; // ya cargado, no refetch

        body.innerHTML = '<p class="text-muted" style="font-size:13px;margin:0">' + t('Cargando…') + '</p>';
        try {
            const html = await this._cargarBloque(clave, nodoId);
            body.innerHTML = html;
            body.dataset.cargado = '1';
        } catch (e) {
            console.error('[FichaFinanzasNodo] error cargando', clave, e);
            body.innerHTML = '<p class="error" style="font-size:13px;margin:0">' + t('No se pudo cargar.') + '</p>';
        }
    },

    _cargarBloque(clave, nodoId) {
        switch (clave) {
            case 'cuenta':
                return this._cargarCuenta(nodoId);
            case 'produccion':
                return this._cargarProduccion(nodoId);
            case 'cortes':
                return this._cargarCortes(nodoId);
            case 'obligaciones':
                return this._cargarObligaciones(nodoId);
            default:
                return Promise.resolve('');
        }
    },

    _fmt(centavos) {
        return MonedaService.format(centavos || 0, this._moneda || undefined);
    },

    // ── Cuenta corriente ────────────────────────────────────────────────────
    async _cargarCuenta(nodoId) {
        const res = await App.fetchWithAuth(`/api/admin/cuentas-corrientes/${nodoId}`);
        // saldo es escalar en céntimos: >0 a favor del nodo, <0 debe.
        const saldo = typeof res?.saldo === 'number' ? res.saldo : 0;
        const aFavor = saldo >= 0;
        const etiqueta = aFavor ? t('A favor') : t('Debe');
        const cls = aFavor ? 'success' : 'error';
        return `
            <dl class="dispositivo-ficha-dl">
                <dt>${t('Saldo')}</dt>
                <dd><strong>${this._fmt(Math.abs(saldo))}</strong>
                    <span class="status-badge ${cls}" style="margin-left:6px">${etiqueta}</span></dd>
            </dl>
            <button class="btn btn-sm btn-secondary" data-action="finanzas-nodo:historialCuenta" data-id="${nodoId}">
                ${icon('list')} ${t('Ver historial')}
            </button>
        `;
    },

    // ── Producción / KPIs ─────────────────────────────────────────────────────
    async _cargarProduccion(nodoId) {
        const res = await App.fetchWithAuth(`/api/admin/nodos/${nodoId}/stats`);
        // { stats: { produccion, financiero, ... } }; sin desde/hasta = acumulado
        // materializado (atribuido). produccion.porCanal trae el desglose Bilhetes/Saldo.
        const p = res?.stats?.produccion || {};
        const f = res?.stats?.financiero || {};
        // jugado_total/pagado_total ya vienen como base ATRIBUIDA desde el endpoint
        // admin stats (admin-nodos-stats-y-analytics.js: jugado_total ← jugado_atribuido)
        // → Ventas − Premios = GGR cuadra. No re-derivar acá.
        // null = red multi-moneda sin tasas: el backend no pudo consolidar. Con
        // `|| 0` la matriz recibía ceros y su regla de "ocultar ceros" borraba las
        // filas ENTERAS — el usuario veía una ficha vacía, sin ningún aviso de que
        // faltaba el dato. Se detecta y se avisa explícitamente.
        // `p.porCanal === null` = el backend no pudo consolidar la MATRIZ (se mide
        // con una query distinta que los escalares: cualquiera de las dos puede ser
        // la que falla). Con el fallback `|| {ceros}` la regla de "ocultar ceros"
        // pintaba una matriz vacía sin aviso.
        const sinConsolidar =
            p.jugado_total === null || p.pagado_total === null || f.recarga_dinero_fresco === null || p.porCanal === null;
        const ventas = p.jugado_total;
        const rtp =
            ventas !== null && p.pagado_total !== null && ventas > 0
                ? ((p.pagado_total / ventas) * 100).toFixed(1)
                : null;
        // El aviso nombra la CAUSA REAL. El backend manda los tres flags separados
        // justamente para poder distinguirlas, y colapsarlos en un texto fijo sobre
        // tasas manda al operador a diagnosticar lo que no es: en prod las tasas
        // están frescas (del día anterior), así que el disparo probable es un nodo
        // sin moneda — un PV borrado con tickets colgando, que se arregla en la
        // ficha del nodo, no cargando cotizaciones.
        const meta = res?.stats?.moneda_meta || {};
        const metaProd = res?.stats?.moneda_meta_produccion || meta;
        const hayNodoSinMoneda = Boolean(meta.nodos_sin_moneda || metaProd.nodos_sin_moneda);
        const motivoDelHueco = hayNodoSinMoneda
            ? t('Há nós sem moeda configurada — o valor deles não pôde ser convertido')
            : t('Rede com moedas mistas e sem taxas de câmbio carregadas — total não confiável');
        // Matriz Bilhetes|Saldo (mismo modelo que POS/web). El "atribuido" no se nombra:
        // Jogaram − Ganharam = Diferença (=GGR) cuadra. Ocultar ceros SIEMPRE.
        const matriz = sinConsolidar
            ? `<div style="border:1px solid var(--border);border-radius:8px;padding:12px;color:var(--text-secondary);font-size:12px;">
                   <span style="color:#f59e0b;font-weight:600;">!</span>
                   ${motivoDelHueco}
               </div>`
            : this._matrizProduccionHTML(p.porCanal, f.recarga_dinero_fresco, f.retiro_dinero_real, rtp);
        return `${matriz}
        <button class="btn btn-sm btn-secondary" data-action="nodos:viewStats" data-id="${nodoId}" style="margin-top:8px;">
            ${icon('bar-chart-2')} ${t('Ver dashboard completo')}
        </button>`;
    },

    /**
     * Matriz por canal de un nodo (igual que TablaProduccionView del POS y el helper
     * produccion-desglose.js de la caja web). Columnas por la regla compartida
     * `canalesDeMatrizParaMostrar` (ticket/wallet siempre, vlt/b2b solo si tuvieron
     * actividad). Filas Jogaram/Ganharam/Diferença, sub Banco/Cash/Bônus/Rejogo del
     * jugado de wallet. Ocultar ceros. Idioma barrista. porCanal viene del endpoint
     * stats (produccion.porCanal): `{ ticket:{jugado,pagado}, wallet:{…}, vlt:{…}, b2b:{…} }`.
     */
    _matrizProduccionHTML(porCanal, recargas, retiros, rtp) {
        const mapa = porCanal || {};
        const columnas = canalesDeMatrizParaMostrar(mapa);
        const idxWallet = columnas.indexOf('wallet');
        const sub = (mapa.wallet && mapa.wallet.subJugado) || {};
        const celda = m => (m === 0 ? '—' : this._fmt(m));
        const fila = (label, valores, cls) => {
            if (valores.every(v => v === 0)) return ''; // ocultar fila en cero
            const celdas = valores.map(v => `<span class="fnz-matriz__val">${celda(v)}</span>`).join('');
            return `<div class="fnz-matriz__fila ${cls || ''}">
                <span class="fnz-matriz__lbl">${ESC(label)}</span>
                ${celdas}
            </div>`;
        };
        // Sub-fila de origen del jugado wallet: cuelga de Jogaram, SOLO bajo la columna wallet.
        const subFila = (label, m, title) => {
            // Igual que en la cascada: se oculta el CERO, nunca el negativo (una anulación deja su
            // bolsa en negativo y las cuatro deben seguir sumando el Jugado wallet).
            if (m === 0) return '';
            const celdas = columnas.map((canal, idx) => (idx === idxWallet ? `<span>${this._fmt(m)}</span>` : '<span></span>')).join('');
            return `<div class="fnz-matriz__subfila"><span${title ? ` title="${ESC(title)}"` : ''}>· ${ESC(label)}</span>${celdas}</div>`;
        };
        const jugados = columnas.map(canal => (mapa[canal] && mapa[canal].jugado) || 0);
        const pagados = columnas.map(canal => (mapa[canal] && mapa[canal].pagado) || 0);
        const ganados = pagados.map(p => -p);
        const diferencias = jugados.map((j, idx) => j - pagados[idx]);
        const encabezado = columnas.map(canal => `<span>${ESC(etiquetaDeCanalDeComision(canal) || canal)}</span>`).join('');
        // Recargas/Retiros no son por canal (movimiento de wallet): el monto va siempre a la
        // ÚLTIMA columna (línea de grid -2) sin importar cuántas columnas de canal haya.
        return `<div class="fnz-matriz" data-canales="${columnas.length}">
            <div class="fnz-matriz__head">
                <span></span>${encabezado}
            </div>
            ${fila(t('Jugado'), jugados)}
            ${subFila(t('Banco'), sub.banco || 0)}
            ${subFila(t('Cash'), sub.cash || 0)}
            ${subFila(t('Bono jugado'), sub.bonus || 0, t('Cuánto se jugó usando saldo de origen bono — no es el monto otorgado'))}
            ${subFila(t('Rejuego'), sub.rejogo || 0)}
            ${fila(t('Ganado'), ganados, 'fnz-matriz__fila--neg')}
            <div class="fnz-matriz__div"></div>
            ${fila(t('Diferencia'), diferencias, 'fnz-matriz__fila--dif')}
            <div class="fnz-matriz__div"></div>
            ${recargas > 0 ? `<div class="fnz-matriz__fila"><span class="fnz-matriz__lbl">${t('Recargas')}</span><span class="fnz-matriz__val" style="grid-column:-2">+${this._fmt(recargas)}</span></div>` : ''}
            ${retiros > 0 ? `<div class="fnz-matriz__fila"><span class="fnz-matriz__lbl">${t('Retiros')}</span><span class="fnz-matriz__val fnz-matriz__val--neg" style="grid-column:-2">-${this._fmt(retiros)}</span></div>` : ''}
            <div class="fnz-matriz__rtp">RTP ${rtp}%</div>
        </div>`;
    },

    // ── Cortes y liquidaciones ────────────────────────────────────────────────
    async _cargarCortes(nodoId) {
        const [cortesRes, liqRes] = await Promise.all([
            App.fetchWithAuth(`/api/admin/cortes?nodoId=${nodoId}&limit=1`),
            App.fetchWithAuth(`/api/admin/liquidaciones?nodoId=${nodoId}&limit=3`),
        ]);
        const cortes = cortesRes?.cortes || [];
        const liquidaciones = liqRes?.liquidaciones || [];

        let html = '';
        if (cortes.length > 0) {
            const c = cortes[0];
            const badge = this._badgeConformidad(c.estado_conformidad);
            html += `<div class="finanzas-nodo-sub">
                <span class="finanzas-nodo-sub-titulo">${t('Último corte')}</span>
                <dl class="dispositivo-ficha-dl">
                    <dt>${t('Fecha')}</dt><dd>${ESC(this._fecha(c.created_at))}</dd>
                    <dt>${t('Neto')}</dt><dd>${this._fmt(c.neto)}</dd>
                    <dt>${t('Estado')}</dt><dd>${badge}</dd>
                </dl>
            </div>`;
        } else {
            html += '<p class="text-muted" style="font-size:13px;margin:0 0 8px">' + t('Sin cortes todavía.') + '</p>';
        }

        if (liquidaciones.length > 0) {
            html += `<div class="finanzas-nodo-sub">
                <span class="finanzas-nodo-sub-titulo">${t('Liquidaciones recientes')}</span>
                <ul class="finanzas-nodo-lista">
                    ${liquidaciones
                        .map(
                            l =>
                                `<li>${this._fmt(l.total_a_pagar)} · ${this._badgeEstadoLiq(l.estado)} <span style="opacity:.6">${ESC(this._fecha(l.created_at))}</span></li>`
                        )
                        .join('')}
                </ul>
            </div>`;
        }

        html += `<button class="btn btn-sm btn-secondary" data-action="finanzas-nodo:irACortes" data-id="${nodoId}">
            ${icon('file-text')} ${t('Gestionar en Finanzas')}
        </button>`;
        return html;
    },

    // ── Obligaciones y adelantos ──────────────────────────────────────────────
    async _cargarObligaciones(nodoId) {
        const [oblRes, advRes] = await Promise.all([
            App.fetchWithAuth(`/api/admin/obligaciones/${nodoId}/efectivas`),
            App.fetchWithAuth(`/api/admin/adelantos?nodoId=${nodoId}&limit=5`),
        ]);
        const obligaciones = oblRes?.obligaciones || [];
        // Adelantos activos: los que aún cuentan (no rechazados/devueltos).
        const adelantos = (advRes?.adelantos || []).filter(a => ['pendiente_conformidad', 'activo'].includes(a.estado));

        let html = '';
        if (obligaciones.length > 0) {
            html += `<div class="finanzas-nodo-sub">
                <span class="finanzas-nodo-sub-titulo">${t('Obligaciones efectivas')} (${obligaciones.length})</span>
                <ul class="finanzas-nodo-lista">
                    ${obligaciones
                        .map(o => {
                            const valor =
                                o.calculo_tipo === 'fijo' && o.monto_fijo != null
                                    ? this._fmt(o.monto_fijo)
                                    : `${o.porcentaje}% ${ESC(o.base || '')}`;
                            const heredada = o.es_heredada
                                ? ` <span style="opacity:.5;font-size:11px">(${t('heredada de')} ${ESC(o.origen_nodo_nombre || '')})</span>`
                                : '';
                            return `<li><strong>${ESC(o.nombre)}</strong> · ${valor}${heredada}</li>`;
                        })
                        .join('')}
                </ul>
            </div>`;
        } else {
            html += '<p class="text-muted" style="font-size:13px;margin:0 0 8px">' + t('Sin obligaciones.') + '</p>';
        }

        if (adelantos.length > 0) {
            html += `<div class="finanzas-nodo-sub">
                <span class="finanzas-nodo-sub-titulo">${t('Adelantos activos')} (${adelantos.length})</span>
                <ul class="finanzas-nodo-lista">
                    ${adelantos
                        .map(a => `<li>${this._fmt(a.monto)} · ${this._badgeEstadoAdelanto(a.estado)}</li>`)
                        .join('')}
                </ul>
            </div>`;
        }
        return html;
    },

    // ── Helpers de presentación ───────────────────────────────────────────────
    _fecha(iso) {
        try {
            return new Date(iso).toLocaleDateString();
        } catch (_e) {
            return '-';
        }
    },

    _badgeConformidad(estado) {
        const M = {
            confirmado: { t: t('Confirmado'), c: 'success' },
            pendiente: { t: t('Pendiente'), c: 'warning' },
            disputado: { t: t('Disputado'), c: 'error' },
            resuelto: { t: t('Resuelto'), c: 'info' },
        };
        const m = M[estado] || { t: estado || '-', c: 'muted' };
        return `<span class="status-badge ${m.c}">${ESC(m.t)}</span>`;
    },

    _badgeEstadoLiq(estado) {
        const M = {
            pagada: { t: t('Pagada'), c: 'success' },
            parcial: { t: t('Parcial'), c: 'warning' },
            pendiente: { t: t('Pendiente'), c: 'warning' },
            compensada: { t: t('Compensada'), c: 'muted' },
        };
        const m = M[estado] || { t: estado || '-', c: 'muted' };
        return `<span class="status-badge ${m.c}">${ESC(m.t)}</span>`;
    },

    _badgeEstadoAdelanto(estado) {
        const M = {
            activo: { t: t('Activo'), c: 'success' },
            pendiente_conformidad: { t: t('Pendiente'), c: 'warning' },
        };
        const m = M[estado] || { t: estado || '-', c: 'muted' };
        return `<span class="status-badge ${m.c}">${ESC(m.t)}</span>`;
    },
};
